import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/Shared/Layout';
import AccountCheckerHeader from '../components/AccountChecker/AccountCheckerHeader';
import DatabaseStats from '../components/AccountChecker/DatabaseStats';
import AccountResults from '../components/AccountChecker/AccountResults';
import SearchOptions from '../components/AccountChecker/SearchOptions';
import SearchHistory from '../components/AccountChecker/SearchHistory';
import PremiumFeatures from '../components/AccountChecker/PremiumFeatures';
import HowItWorks from '../components/AccountChecker/HowItWorks';
import PageTransition from '../components/Shared/PageTransition';
// Import Hooks and Contexts
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';

// Import Config
import { WS_ACCOUNT_CHECKER_TYPES, WS_ACCOUNT_CHECKER_TOPICS } from '../config/wsAccountCheckerConfig';
import { useNotifications } from '../contexts/NotificationContext';
import { handleWebSocketError } from '../utils/websocketErrorHandler';

// --- Mock Data (Replace with fetched data if needed) ---
const initialStats = {
  totalAccounts: '15.3M',
  uniquePasswords: '9.8M',
  serverSources: '2,800+',
  successRate: '72%',
};

// --- ---

const AccountChecker = ({ openModal }) => {
  const { isAuthenticated, user } = useAuth();
  const { isConnected, sendMessage, subscribe } = useWebSocket();
  const { addNotification } = useNotifications();

  // --- State Management ---
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchStatus, setSearchStatus] = useState('Idle');
  const [searchProgress, setSearchProgress] = useState(0);
  const [finalResultCount, setFinalResultCount] = useState(0);

  // Search Configuration
  const [searchOptions, setSearchOptions] = useState({
    database: 'all',
    searchType: 'exact',
    maxResults: 20,
    showConfidence: true,
  });

  // Other UI State
  const [stats, setStats] = useState(initialStats);
  const [searchHistory, setSearchHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  // WebSocket Subscription Management
  const subscriptionsRef = useRef({ results: null, errors: null, history: null });
  // --- FIX: Rename historyRequestedRef to reflect its purpose better and manage it per-page-instance ---
  const historyFetchAttemptedRef = useRef(false); // Renamed and used to track if fetch was attempted *this session/mount*

  // --- Manage Search History ---
  const addToHistory = useCallback((term, resultsCount) => {
    if (!term) return;
    setSearchHistory(prev => {
      const newHistory = prev.filter(item => item.username.toLowerCase() !== term.toLowerCase());
      const newItem = {
        username: term,
        results: resultsCount,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        avatar: `https://minotar.net/avatar/${term}/32`
      };
      const updatedHistory = [newItem, ...newHistory].slice(0, 10);
      return updatedHistory;
    });
  }, []);

  // --- WebSocket Message Handlers ---
  const handleResultsMessage = useCallback((message, wsError) => {
    if (wsError) {
      setIsLoading(false);
      setSearchStatus('Error');
      const errorMsg = handleWebSocketError(
        message, wsError, openModal, addNotification,
        { defaultTitle: 'Account Check Error', moduleName: 'Account Checker' }
      );
      setError(errorMsg);
      return;
    }

    console.log("Account Checker WS Message Received:", message);

    switch (message?.type) {
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_INITIATED:
        setSearchStatus('Initiated');
        setIsLoading(true);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_PROGRESS:
        setSearchStatus(message.status || 'Searching...');
        if (message.percentage !== undefined) setSearchProgress(message.percentage);
        setIsLoading(true);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_RESULT:
        if (message.data) setSearchResults(prev => [...prev, message.data]);
        else console.warn("Received CHECK_RESULT without data:", message);
        setIsLoading(true);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_BATCH_RESULT:
        setSearchResults(prev => [...prev, ...(message.results || [])]);
        setIsLoading(true);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_NO_RESULTS:
        setSearchStatus(`No results found for "${currentSearchTerm}"`);
        setSearchResults([]);
        setIsLoading(false);
        setSearchProgress(100);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_COMPLETED:
        setIsLoading(false);
        setSearchProgress(100);
        break;
      default:
        console.warn("Received unknown message type on results topic:", message?.type);
    }
  }, [currentSearchTerm, openModal, addNotification]);

  const handleErrorMessage = useCallback((errorPayload, wsError) => {
    const errorMsg = handleWebSocketError(
      errorPayload, wsError, openModal, addNotification,
      { defaultTitle: 'Account Check Error', moduleName: 'Account Checker' }
    );
    setError(errorMsg);
    setIsLoading(false);
    setSearchStatus('Error');
    setSearchProgress(0);
  }, [openModal, addNotification]);

  const handleHistoryMessage = useCallback((message, wsError) => {
    setIsHistoryLoading(false); // Stop loading when message arrives (success or error)
    if (wsError || message?.type === WS_ACCOUNT_CHECKER_TYPES.HISTORY_ERROR) {
      const errorMsg = message?.error || wsError?.message || "Failed to load search history.";
      console.error("Account Check History WS Error:", errorMsg);
      setHistoryError(errorMsg);
      setSearchHistory([]);
    } else if (message?.type === WS_ACCOUNT_CHECKER_TYPES.HISTORY_LIST && message.data?.history) {
      console.log("Received Account Check History:", message.data.history);
      setSearchHistory(message.data.history);
      setHistoryError(null);
    } else {
      console.warn("Received unknown/invalid history message:", message);
      // Don't set an error here necessarily, maybe just log it.
      // If we received *something* but not the expected format, it might be an issue.
      // Let's keep existing history unless explicitly cleared by an error.
      // setHistoryError("Received unexpected history data format.");
      // setSearchHistory([]);
    }
  }, []);

  // --- WebSocket Subscription Effect ---
  useEffect(() => {
    // --- FIX: Reset history fetch attempt flag when disconnected or logged out ---
    if (!isConnected || !isAuthenticated) {
      Object.values(subscriptionsRef.current).forEach(unsub => unsub?.());
      subscriptionsRef.current = { results: null, errors: null, history: null };
      historyFetchAttemptedRef.current = false; // Reset flag here
      if (isLoading) {
        setError("Connection lost during search.");
        setIsLoading(false);
        setSearchStatus('Error');
      }
      setIsHistoryLoading(false); // Ensure loading spinner stops if disconnected
      return; // Stop further execution
    }

    // Subscribe if connected and authenticated
    let didSubscribe = false; // Track if any new subscriptions were made in this run

    if (!subscriptionsRef.current.results) {
      console.log("Subscribing to Account Checker results...");
      subscriptionsRef.current.results = subscribe(
        `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.RESULTS}`,
        handleResultsMessage
      );
      didSubscribe = true;
    }
    if (!subscriptionsRef.current.errors) {
      console.log("Subscribing to Account Checker errors...");
      subscriptionsRef.current.errors = subscribe(
        `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.ERRORS}`,
        handleErrorMessage
      );
      didSubscribe = true;
    }
    if (!subscriptionsRef.current.history) {
      console.log("Subscribing to Account Checker history...");
      subscriptionsRef.current.history = subscribe(
        `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.USER_HISTORY}`,
        handleHistoryMessage
      );
      didSubscribe = true;
    }

    // --- FIX: Request history if connected AND fetch hasn't been attempted *in this component instance* ---
    // Check the ref before attempting fetch
    if (!historyFetchAttemptedRef.current) {
      setIsHistoryLoading(true);
      setHistoryError(null);
      console.log("Requesting Account Check History...");
      sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}${WS_ACCOUNT_CHECKER_TOPICS.APP_GET_HISTORY}`, {});
      historyFetchAttemptedRef.current = true; // Mark as attempted for this mount/session
    } else if (didSubscribe) {
        // If we re-subscribed (e.g., after quick disconnect/reconnect) but didn't re-request history,
        // ensure the loading state is false if no request is pending.
        // This check might be overly cautious but helps prevent stale loading state.
        // We rely on handleHistoryMessage to set loading to false upon receiving data/error.
        // setIsHistoryLoading(false); // Reconsider if this causes issues
    }


    // Cleanup function
    return () => {
      console.log("Cleaning up Account Checker subscriptions (effect cleanup)...");
      Object.values(subscriptionsRef.current).forEach(unsub => unsub?.());
      subscriptionsRef.current = { results: null, errors: null, history: null };
      // --- FIX: Reset the history fetch attempt flag on unmount ---
      // This ensures history is fetched again if the user navigates away and back
      historyFetchAttemptedRef.current = false;
    };
    // Added sendMessage to dependencies
  }, [isConnected, isAuthenticated, subscribe, sendMessage, handleResultsMessage, handleErrorMessage, handleHistoryMessage]);

  // --- Search Action ---
  const handleSearch = useCallback((username) => {
    const trimmedUsername = username.trim();
    if (!trimmedUsername) { setError("Please enter a username."); return; }
    if (!isAuthenticated) { setError("Please log in to perform a search."); return; }
    if (!isConnected) { setError("Not connected to the server. Please wait or refresh."); return; }
    if (isLoading) { console.warn("Search requested while already loading."); return; }

    console.log(`Starting search for: ${trimmedUsername} with options:`, searchOptions);

    setSearchQuery(trimmedUsername);
    setCurrentSearchTerm(trimmedUsername);
    setSearchResults([]);
    setError(null);
    setIsLoading(true);
    setSearchStatus('Initiating...');
    setSearchProgress(0);

    sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}/start`, { username: trimmedUsername, options: searchOptions });

  }, [isAuthenticated, isConnected, isLoading, searchOptions, sendMessage]); // Removed openModal dependency as it's handled by error handler

  // useEffect for handling completion and history update
  useEffect(() => {
    if (!isLoading && currentSearchTerm && !['Error'].includes(searchStatus)) {
        if (searchResults.length > 0 && !searchStatus.startsWith('Search complete')) {
            setSearchStatus(`Search complete. Found ${searchResults.length} result(s).`);
            addToHistory(currentSearchTerm, searchResults.length);
        } else if (searchResults.length === 0 && searchStatus.startsWith('No results found')) {
            // Status already set by backend, just add history
            addToHistory(currentSearchTerm, 0);
        } else if (searchResults.length === 0 && !searchStatus.startsWith('No results found') && !searchStatus.startsWith('Search complete')) {
             // Fallback if no results message didn't arrive but search finished
             setSearchStatus(`Search complete. No results found for "${currentSearchTerm}".`);
             addToHistory(currentSearchTerm, 0);
        }
    }
  }, [isLoading, searchResults, currentSearchTerm, searchStatus, addToHistory]);

  // --- Update Search Options ---
  const handleOptionChange = useCallback((optionKey, value) => {
    console.log("Option change:", optionKey, value);
    setSearchOptions(prev => ({ ...prev, [optionKey]: value }));
  }, []);

  const handleHistoryItemClick = (username) => {
    handleSearch(username);
  };

  // Function to clear the current search state and results
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setCurrentSearchTerm('');
    setSearchResults([]);
    setError(null);
    setIsLoading(false);
    setSearchStatus('Idle');
    setSearchProgress(0);
  }, []);


  // --- Render Logic ---
  return (
    <PageTransition>
      <div className="p-6 lg:p-8">
        <AccountCheckerHeader
            onSearch={handleSearch}
            isLoading={isLoading}
            initialValue={searchQuery}
        />
        <DatabaseStats stats={stats} />

        {/* --- FIX: Added flex flex-col to the grid item for AccountResults --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
           <div className="lg:col-span-2 flex flex-col"> {/* Apply flex-col here */}
              <AccountResults
                results={searchResults}
                isLoading={isLoading}
                searchQuery={currentSearchTerm}
                error={error}
                status={searchStatus}
                onNewSearch={handleClearSearch}
                showConfidence={searchOptions.showConfidence}
              />
           </div>
          <div className="lg:col-span-1 space-y-6">
            <SearchOptions options={searchOptions} onOptionChange={handleOptionChange} />
            <SearchHistory
              history={searchHistory}
              onHistoryItemClick={handleHistoryItemClick}
              isLoading={isHistoryLoading}
              error={historyError}
            />
          </div>
        </div>

        <PremiumFeatures />
        <HowItWorks />
      </div>
    </PageTransition>
  );
};

export default AccountChecker;